import { fetchProductSearch } from '@theme/vespa-search';
import ResultsList from './results-list.js';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that extends ResultsList to support Vespa-powered search
 */
export default class SearchPage extends ResultsList {
  /** @type {IntersectionObserver | undefined} */
  #infinityScrollObserver;

  /** @type {boolean} */
  #loadingNextPage = false;

  /** @type {boolean} */
  #loadingPreviousPage = false;

  /** @type {number} - Highest page number currently loaded */
  #maxLoadedPage = 0;

  /** @type {number} - Lowest page number currently loaded */
  #minLoadedPage = 0;

  async connectedCallback() {
    // Only call super if NOT using Vespa search OR if there's no search query
    if (!this.useVespaSearch || !this.searchQuery) {
      super.connectedCallback();
      return;
    }

    // For Vespa search, we need to manually call Component's connectedCallback
    // to get refs initialized, but skip PaginatedList's pagination setup
    if (this.refs === undefined) {
      this.refs = {};
    }
    this.#setupRefs();

    // Show loading indicator and hide native results
    this.#showLoadingIndicator();

    // Fetch and render Vespa results
    await this.#fetchAndRenderVespaResults();

    // Initialize loaded page tracking
    this.#maxLoadedPage = this.currentPage;
    this.#minLoadedPage = this.currentPage;

    // Setup load previous button
    this.#setupLoadPreviousButton();

    // Setup infinite scroll for Vespa pagination
    this.#setupInfiniteScroll();
  }

  disconnectedCallback() {
    if (this.#infinityScrollObserver) {
      this.#infinityScrollObserver.disconnect();
    }
  }

  /**
   * Setup refs manually (from Component class)
   */
  #setupRefs() {
    const refs = this.querySelectorAll('[ref]');
    refs.forEach((el) => {
      const refName = el.getAttribute('ref');
      if (!refName) return;

      if (refName.endsWith('[]')) {
        const arrayRefName = refName.slice(0, -2);
        if (!this.refs[arrayRefName]) {
          this.refs[arrayRefName] = [];
        }
        this.refs[arrayRefName].push(el);
      } else {
        this.refs[refName] = el;
      }
    });
  }

  /**
   * Show loading skeleton and hide native results to prevent flash
   */
  #showLoadingIndicator() {
    const { grid } = this.refs;
    if (!grid) return;

    // Create skeleton cards and insert them directly into the existing grid
    const skeletonCards = Array.from({ length: 24 }, (_, i) => {
      const skeletonItem = document.createElement('li');
      skeletonItem.className = `product-grid__item product-grid-skeleton__item`;
      skeletonItem.setAttribute('data-skeleton-item', 'initial');
      skeletonItem.innerHTML = `
        <div class="product-grid-skeleton__card">
          <div class="product-grid-skeleton__image"></div>
          <div class="product-grid-skeleton__content">
            <div class="product-grid-skeleton__title"></div>
            <div class="product-grid-skeleton__price"></div>
          </div>
        </div>
      `;
      return skeletonItem;
    });

    // Clear the grid and insert skeleton cards
    grid.innerHTML = '';
    skeletonCards.forEach(card => grid.appendChild(card));
  }

  /**
   * Hide loading indicator and show results
   */
  #hideLoadingIndicator() {
    const skeletonItems = this.querySelectorAll('[data-skeleton-item="initial"]');
    skeletonItems.forEach(item => {
      item.classList.add('is-removing');
      setTimeout(() => item.remove(), 300);
    });

    // Remove loading class and add loaded class to show results
    this.classList.remove('search-page--vespa-loading');
    this.classList.add('search-page--vespa-loaded');
  }

  /**
   * Check if Vespa search is enabled via settings
   * @returns {boolean}
   */
  get useVespaSearch() {
    return this.dataset.useVespa === 'true';
  }

  /**
   * Get the search query from the URL
   * @returns {string}
   */
  get searchQuery() {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || '';
  }

  /**
   * Get the current page number from the URL (0-based)
   * @returns {number}
   */
  get currentPage() {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page') || '1', 10);
    // Convert from 1-based URL param to 0-based Vespa page
    return page - 1;
  }

  /**
   * Get the page size (items per page)
   * @returns {number}
   */
  get pageSize() {
    return 96;
  }

  /**
   * Setup load previous button
   */
  #setupLoadPreviousButton() {
    const { loadPreviousButton, loadPreviousWrapper } = this.refs;

    if (!loadPreviousButton || !loadPreviousWrapper) {
      console.warn('[SearchPage] No loadPreviousButton found');
      return;
    }

    // Show button if we're on page 2 or higher
    if (this.currentPage > 0) {
      loadPreviousWrapper.style.display = 'flex';
    }

    // Add click handler
    loadPreviousButton.addEventListener('click', async () => {
      await this.#loadPreviousPage();
    });

    console.log('[SearchPage] Load previous button setup complete');
  }

  /**
   * Setup infinite scroll observer for Vespa pagination
   * Only observes forward pagination (next page) - previous pages use button clicks
   */
  #setupInfiniteScroll() {
    const { viewMoreNext } = this.refs;

    // Create observer for forward pagination only
    this.#infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#loadingNextPage) {
            await this.#loadNextPage();
          }
        }
      },
      {
        rootMargin: '400px', // Start loading before user reaches the end
      }
    );

    // Observe viewMoreNext for forward pagination
    if (viewMoreNext) {
      this.#infinityScrollObserver.observe(viewMoreNext);
      console.log('[SearchPage] Observing viewMoreNext for forward pagination');
    } else {
      console.warn('[SearchPage] No viewMoreNext element found');
    }

    console.log('[SearchPage] Infinite scroll setup complete (forward only)');
  }

  /**
   * Show skeleton loading indicator for pagination
   * @param {'next' | 'previous'} position - Where to show the skeleton
   */
  #showPaginationLoadingIndicator(position) {
    const { grid } = this.refs;
    if (!grid) return;

    // Create skeleton cards and insert them directly into the existing grid
    const skeletonCards = Array.from({ length: 12 }, (_, i) => {
      const skeletonItem = document.createElement('li');
      skeletonItem.className = `product-grid__item product-grid-skeleton__item`;
      skeletonItem.setAttribute('data-skeleton-item', position);
      skeletonItem.innerHTML = `
        <div class="product-grid-skeleton__card">
          <div class="product-grid-skeleton__image"></div>
          <div class="product-grid-skeleton__content">
            <div class="product-grid-skeleton__title"></div>
            <div class="product-grid-skeleton__price"></div>
          </div>
        </div>
      `;
      return skeletonItem;
    });

    if (position === 'next') {
      skeletonCards.forEach(card => grid.appendChild(card));
    } else {
      skeletonCards.reverse().forEach(card => grid.insertBefore(card, grid.firstChild));
    }
  }

  /**
   * Hide skeleton loading indicator for pagination
   * @param {'next' | 'previous'} position - Which skeleton to hide
   */
  #hidePaginationLoadingIndicator(position) {
    const skeletonItems = this.querySelectorAll(`[data-skeleton-item="${position}"]`);
    skeletonItems.forEach(item => {
      item.classList.add('is-removing');
      setTimeout(() => item.remove(), 300);
    });
  }

  /**
   * Load the next page of results from Vespa
   */
  async #loadNextPage() {
    if (this.#loadingNextPage) return;

    const nextPage = this.#maxLoadedPage + 1;
    console.log('[SearchPage] Loading next page:', nextPage);

    this.#loadingNextPage = true;
    this.#showPaginationLoadingIndicator('next');

    try {
      const searchTerm = this.searchQuery;

      // Fetch next page from Vespa
      const { handles, totalCount, error } = await fetchProductSearch(searchTerm, {
        page: nextPage,
        pageSize: this.pageSize
      });

      if (error || handles.length === 0) {
        console.log('[SearchPage] No more results to load');
        // Hide the viewMoreNext element since there are no more pages
        if (this.refs.viewMoreNext) {
          this.refs.viewMoreNext.style.display = 'none';
        }
        return;
      }

      console.log('[SearchPage] Loaded', handles.length, 'products for page', nextPage);

      // Build search query with handles
      const searchQuery = handles.map(handle => `handle:${handle}`).join(' OR ');

      // Fetch rendered HTML for these products
      // Always use page 1 since we're fetching all matching handles at once
      const url = new URL(Theme.routes.search_url, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('page', '1'); // Always page 1 since our query contains all handles for this page

      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.getAttribute('section-id'),
        false,
        url
      );

      if (!resultsMarkup) {
        console.error('[SearchPage] Failed to get section HTML for next page');
        return;
      }

      // Parse and extract product items
      const parser = new DOMParser();
      const doc = parser.parseFromString(resultsMarkup, 'text/html');
      const gridElement = doc.querySelector('[ref="grid"]');

      if (!gridElement) {
        console.error('[SearchPage] No grid found in results');
        console.log('[SearchPage] Response HTML:', resultsMarkup.substring(0, 500));
        return;
      }

      // Get all product items and reorder them
      const items = Array.from(gridElement.querySelectorAll('[ref="cards[]"]'));
      const itemMap = new Map();

      items.forEach(item => {
        const productCard = item.querySelector('product-card');
        const handle = productCard?.getAttribute('data-product-handle');
        if (handle) {
          itemMap.set(handle, item);
          // Update data-page attribute to reflect the Vespa page
          item.setAttribute('data-page', (nextPage + 1).toString());
        }
      });

      // Append items in Vespa order
      const { grid } = this.refs;
      if (grid) {
        handles.forEach(handle => {
          const item = itemMap.get(handle);
          if (item) {
            grid.appendChild(item);
          }
        });

        console.log('[SearchPage] Appended', handles.length, 'items to grid');
      }

      // Update loaded page tracking
      this.#maxLoadedPage = nextPage;

      // Update URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', (nextPage + 1).toString());
      history.pushState('', '', newUrl.toString());

    } catch (error) {
      console.error('[SearchPage] Error loading next page:', error);
    } finally {
      this.#hidePaginationLoadingIndicator('next');
      this.#loadingNextPage = false;
    }
  }

  /**
   * Load the previous page of results from Vespa
   */
  async #loadPreviousPage() {
    if (this.#loadingPreviousPage) return;

    const previousPage = this.#minLoadedPage - 1;

    // Don't load page before 0
    if (previousPage < 0) {
      console.log('[SearchPage] Already at first page');
      if (this.refs.viewMorePrevious) {
        this.refs.viewMorePrevious.style.display = 'none';
      }
      if (this.refs.loadPreviousWrapper) {
        this.refs.loadPreviousWrapper.style.display = 'none';
      }
      return;
    }

    console.log('[SearchPage] Loading previous page:', previousPage);

    this.#loadingPreviousPage = true;
    this.#showPaginationLoadingIndicator('previous');

    try {
      const searchTerm = this.searchQuery;

      // Fetch previous page from Vespa
      const { handles, totalCount, error } = await fetchProductSearch(searchTerm, {
        page: previousPage,
        pageSize: this.pageSize
      });

      if (error || handles.length === 0) {
        console.log('[SearchPage] No previous results to load');
        if (this.refs.viewMorePrevious) {
          this.refs.viewMorePrevious.style.display = 'none';
        }
        return;
      }

      console.log('[SearchPage] Loaded', handles.length, 'products for page', previousPage);

      // Build search query with handles
      const searchQuery = handles.map(handle => `handle:${handle}`).join(' OR ');

      // Fetch rendered HTML for these products
      // Always use page 1 since we're fetching all matching handles at once
      const url = new URL(Theme.routes.search_url, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('page', '1'); // Always page 1 since our query contains all handles for this page

      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.getAttribute('section-id'),
        false,
        url
      );

      if (!resultsMarkup) {
        console.error('[SearchPage] Failed to get section HTML for previous page');
        return;
      }

      // Parse and extract product items
      const parser = new DOMParser();
      const doc = parser.parseFromString(resultsMarkup, 'text/html');
      const gridElement = doc.querySelector('[ref="grid"]');

      if (!gridElement) {
        console.error('[SearchPage] No grid found in results');
        console.log('[SearchPage] Response HTML:', resultsMarkup.substring(0, 500));
        return;
      }

      // Get all product items and reorder them
      const items = Array.from(gridElement.querySelectorAll('[ref="cards[]"]'));
      const itemMap = new Map();

      items.forEach(item => {
        const productCard = item.querySelector('product-card');
        const handle = productCard?.getAttribute('data-product-handle');
        if (handle) {
          itemMap.set(handle, item);
          // Update data-page attribute to reflect the Vespa page
          item.setAttribute('data-page', (previousPage + 1).toString());
        }
      });

      // Get current scroll position and first product position
      const scrollY = window.scrollY;
      const { grid } = this.refs;
      const firstProduct = grid?.querySelector('[ref="cards[]"]');
      const firstProductOffsetTop = firstProduct?.offsetTop || 0;

      // Prepend items in Vespa order
      if (grid) {
        // Create document fragment for better performance
        const fragment = document.createDocumentFragment();

        handles.forEach(handle => {
          const item = itemMap.get(handle);
          if (item) {
            fragment.appendChild(item);
          }
        });

        // Prepend all items at once
        grid.insertBefore(fragment, grid.firstChild);

        console.log('[SearchPage] Prepended', handles.length, 'items to grid');

        // Maintain scroll position by calculating height difference
        requestAnimationFrame(() => {
          const newFirstProduct = grid.querySelector('[ref="cards[]"]');
          const newFirstProductOffsetTop = newFirstProduct?.offsetTop || 0;
          const heightDiff = firstProductOffsetTop - newFirstProductOffsetTop;

          // Adjust scroll position to maintain visual continuity
          window.scrollTo(0, scrollY + heightDiff);
        });
      }

      // Update loaded page tracking
      this.#minLoadedPage = previousPage;

      // Hide load previous button if we've reached page 0
      if (previousPage === 0 && this.refs.loadPreviousWrapper) {
        this.refs.loadPreviousWrapper.style.display = 'none';
      }

      // Update URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', (previousPage + 1).toString());
      history.pushState('', '', newUrl.toString());

    } catch (error) {
      console.error('[SearchPage] Error loading previous page:', error);
    } finally {
      this.#hidePaginationLoadingIndicator('previous');
      this.#loadingPreviousPage = false;
    }
  }

  /**
   * Fetch results from Vespa and render them using Shopify section rendering
   */
  async #fetchAndRenderVespaResults() {
    const searchTerm = this.searchQuery;

    if (!searchTerm) {
      console.log('[SearchPage] No search term provided');
      return;
    }

    console.log('[SearchPage] Fetching Vespa results for:', searchTerm, 'page:', this.currentPage);

    try {
      // Fetch product handles from Vespa with pagination
      const { handles, totalCount, error } = await fetchProductSearch(searchTerm, {
        page: this.currentPage,
        pageSize: this.pageSize
      });

      if (error) {
        console.error('[SearchPage] Vespa search error:', error);
        return;
      }

      console.log('[SearchPage] Vespa returned', handles.length, 'products out of', totalCount, 'total');

      if (handles.length === 0) {
        console.log('[SearchPage] No products found, showing empty state');
        return;
      }

      // Build a Shopify search query with the Vespa-provided handles
      const searchQuery = handles.map(handle => `handle:${handle}`).join(' OR ');

      // Use Shopify section rendering to get the HTML with these products
      // Always use page 1 since we're fetching all matching handles at once
      const url = new URL(Theme.routes.search_url, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('page', '1'); // Always page 1 since our query contains all handles for this page

      console.log('[SearchPage] Fetching Shopify section HTML with Vespa handles');

      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.getAttribute('section-id'),
        false,
        url
      );

      if (!resultsMarkup) {
        console.error('[SearchPage] Failed to get section HTML');
        return;
      }

      // Parse the results and update the product grid
      const parser = new DOMParser();
      const doc = parser.parseFromString(resultsMarkup, 'text/html');
      const newGridWrapper = doc.querySelector('.collection-wrapper');

      if (!newGridWrapper) {
        console.error('[SearchPage] Could not find .collection-wrapper in results');
        return;
      }

      // Reorder products to match Vespa order
      this.#reorderProductCards(newGridWrapper, handles);

      // Morph the existing grid with the new results
      const currentGridWrapper = this.querySelector('.collection-wrapper');
      if (currentGridWrapper && newGridWrapper) {
        morph(currentGridWrapper, newGridWrapper);
        console.log('[SearchPage] Results updated successfully');
      }

    } catch (error) {
      console.error('[SearchPage] Error fetching Vespa results:', error);
    } finally {
      // Always hide the loading indicator
      this.#hideLoadingIndicator();
    }
  }

  /**
   * Reorder product cards in a container to match Vespa's order
   * @param {Element} gridWrapper - The grid wrapper containing products
   * @param {string[]} orderedHandles - Array of handles in desired order
   */
  #reorderProductCards(gridWrapper, orderedHandles) {
    console.log('[SearchPage] Reordering products to match Vespa order');

    // Find the product grid within the wrapper
    const productGrid = gridWrapper.querySelector('[ref="grid"]');
    if (!productGrid) {
      console.warn('[SearchPage] No product grid found for reordering');
      return;
    }

    // Extract all product items
    const items = Array.from(productGrid.querySelectorAll('[ref="cards[]"]'));
    console.log('[SearchPage] Found', items.length, 'product items to reorder');

    // Build handle → item map
    const itemMap = new Map();
    items.forEach(item => {
      const productCard = item.querySelector('product-card');
      const handle = productCard?.getAttribute('data-product-handle');
      if (handle) {
        itemMap.set(handle, item);
        console.log('[SearchPage] Mapped handle:', handle);
      }
    });

    // Clear grid
    productGrid.innerHTML = '';

    // Append items in Vespa order
    let reorderedCount = 0;
    orderedHandles.forEach(handle => {
      const item = itemMap.get(handle);
      if (item) {
        productGrid.appendChild(item);
        reorderedCount++;
      } else {
        console.warn('[SearchPage] No item found for handle:', handle);
      }
    });

    console.log('[SearchPage] Reordered', reorderedCount, 'items to match Vespa order');
  }
}

if (!customElements.get('search-page')) {
  customElements.define('search-page', SearchPage);
}
